const normalizeValue = value => {
  if (value === null || value === undefined) return null;
  const trimmed = `${value}`.trim();
  return trimmed === '' ? null : trimmed;
};

const safeMessage = (intl, id) => {
  const message = intl.formatMessage({ id });
  return message.includes(id) ? null : message;
};

const formatUnit = (intl, value, unitId) => {
  const base = normalizeValue(value);
  if (!base) return null;
  const unit = safeMessage(intl, unitId);
  return unit ? `${base} ${unit}` : `${base}`;
};

const formatEnum = (intl, idBase, value) => {
  const base = normalizeValue(value);
  if (!base) return null;
  const label = safeMessage(intl, `${idBase}.${base}`);
  return label || base;
};

const sizeFieldLabels = {
  length: 'ListingFacts.label.length',
  beam: 'ListingFacts.label.beam',
  draught: 'ListingFacts.label.draught',
  clearance: 'ListingFacts.label.clearance',
  weight: 'ListingFacts.label.weight',
};

const fieldValueConfig = {
  length: {
    get: publicData =>
      publicData?.length ?? publicData?.boatData?.dimensions?.length ?? publicData?.loa,
    format: (intl, value) => formatUnit(intl, value, 'ListingFacts.unit.m'),
  },
  hullMaterial: {
    get: publicData => publicData?.hullMaterial ?? publicData?.boatData?.hull?.material,
    format: (intl, value) => formatEnum(intl, 'General.listing.material', value),
  },
  fuelType: {
    get: publicData =>
      publicData?.fuelType ??
      publicData?.engineData?.engine?.fuel ??
      publicData?.boatEngines?.find(e => e?.fuelType)?.fuelType,
    format: (intl, value) => formatEnum(intl, 'General.listing.fuelType', value),
  },
  yearBuilt: {
    get: publicData => publicData?.yearBuilt ?? publicData?.boatData?.yearBuilt,
    format: (_intl, value) => normalizeValue(value),
  },
  clearance: {
    get: publicData => publicData?.clearance ?? publicData?.boatData?.dimensions?.clearance,
    format: (intl, value) => formatUnit(intl, value, 'ListingFacts.unit.m'),
  },
  enginePower: {
    get: publicData =>
      publicData?.engineData?.engine?.power ??
      publicData?.boatEngines?.find(e => e?.enginePower)?.enginePower,
    format: (intl, value) => formatUnit(intl, value, 'ListingFacts.unit.pk'),
  },
  beam: {
    get: publicData => publicData?.beam ?? publicData?.boatData?.dimensions?.beam,
    format: (intl, value) => formatUnit(intl, value, 'ListingFacts.unit.m'),
  },
  draught: {
    get: publicData => publicData?.draught ?? publicData?.boatData?.dimensions?.draught,
    format: (intl, value) => formatUnit(intl, value, 'ListingFacts.unit.m'),
  },
  keelType: {
    get: publicData => publicData?.keelType ?? publicData?.boatData?.hull?.keelType,
    format: (intl, value) => formatEnum(intl, 'General.listing.keelType', value),
  },
  weight: {
    get: publicData =>
      publicData?.weight ?? publicData?.displacement ?? publicData?.boatData?.weight,
    format: (intl, value) => formatUnit(intl, value, 'ListingFacts.unit.kg'),
  },
  persons: {
    get: publicData =>
      publicData?.passengerCapacity ??
      publicData?.persons ??
      publicData?.boatData?.capacity?.persons,
    format: (_intl, value) => normalizeValue(value),
  },
  engineFuel: {
    get: publicData =>
      publicData?.engineData?.engine?.fuel ??
      publicData?.boatEngines?.find(e => e?.fuelType)?.fuelType,
    format: (intl, value) => formatEnum(intl, 'General.listing.fuelType', value),
  },
  engineHours: {
    get: publicData =>
      publicData?.engineData?.engine?.hours ??
      publicData?.boatEngines?.find(e => e?.engineHours)?.engineHours,
    format: (intl, value) => formatUnit(intl, value, 'ListingFacts.unit.hours'),
  },
};

const categoryAliases = {
  power: ['powerBoats'],
  sail: ['sailingBoats'],
  inflatable: ['inflatableBoats'],
  small: ['smallBoats'],
  pwc: ['jetski', 'jetSki'],
};

const resolveFamily = category => {
  if (!category) return null;
  if (categoryAliases[category]) return category;
  return Object.keys(categoryAliases).find(key => categoryAliases[key].includes(category)) || null;
};

export const buildListingFactsLine = (intl, publicData, config, options = {}) => {
  if (!publicData || !config) return null;

  const { familyFields = {}, overrides = [] } = config;
  const { labelSizes } = options || {};
  const category = publicData?.category;
  const type = publicData?.type;
  const family = resolveFamily(category) || category;

  const baseFields = familyFields[family] || [];
  const finalFields = [...baseFields];

  const override = overrides.find(item => {
    if (item.type !== type) return false;
    if (item.category === family) return true;
    const aliases = categoryAliases[item.category] || [];
    return aliases.includes(category);
  });

  if (override?.replace) {
    Object.entries(override.replace).forEach(([index, fieldKey]) => {
      const idx = Number(index);
      if (!Number.isNaN(idx) && idx >= 0) {
        finalFields[idx] = fieldKey;
      }
    });
  }

  const uniqueFields = [];
  finalFields.forEach(key => {
    if (!key || uniqueFields.includes(key)) return;
    uniqueFields.push(key);
  });

  const parts = uniqueFields
    .map(key => {
      const field = fieldValueConfig[key];
      if (!field) return null;
      const value = field.get(publicData);
      const formatted = field.format(intl, value);
      if (!formatted) return null;
      if (labelSizes && sizeFieldLabels[key]) {
        const label = safeMessage(intl, sizeFieldLabels[key]);
        return label ? `${label}: ${formatted}` : formatted;
      }
      return formatted;
    })
    .filter(Boolean);

  const { maxParts } = options || {};
  const limitedParts =
    Number.isFinite(maxParts) && maxParts > 0 ? parts.slice(0, maxParts) : parts;

  return limitedParts.length ? limitedParts.join(' \u00b7 ') : null;
};

export const listingFactsConfig = {
  familyFields: {
    power: ['length', 'hullMaterial', 'fuelType', 'yearBuilt', 'beam', 'clearance'],
    sail: ['length', 'hullMaterial', 'fuelType', 'yearBuilt', 'draught', 'clearance'],
    inflatable: ['length', 'hullMaterial', 'fuelType', 'yearBuilt', 'beam', 'enginePower'],
    small: ['length', 'hullMaterial', 'fuelType', 'yearBuilt', 'beam', 'weight'],
    pwc: ['length', 'hullMaterial', 'fuelType', 'yearBuilt', 'enginePower', 'persons'],
  },
  overrides: [
    { category: 'power', type: 'sportboat', replace: { 5: 'enginePower' } },
    { category: 'power', type: 'speedboat', replace: { 5: 'enginePower' } },
    { category: 'power', type: 'houseboat', replace: { 5: 'beam' } },
    { category: 'power', type: 'open_motorboat', replace: { 5: 'draught' } },
    { category: 'sail', type: 'catamaran', replace: { 4: 'beam' } },
    { category: 'sail', type: 'dinghy', replace: { 5: 'keelType' } },
    { category: 'sail', type: 'flat_bottom', replace: { 5: 'keelType' } },
    { category: 'inflatable', type: 'tender', replace: { 4: 'weight' } },
    { category: 'small', type: 'rowboat', replace: { 4: 'persons' } },
    { category: 'small', type: 'canoe', replace: { 4: 'weight' } },
    { category: 'pwc', type: 'stand_up', replace: { 5: 'weight' } },
  ],
};
